if (!customElements.get('menu-content-product-push')) {
  customElements.define(
    'menu-content-product-push',
    class MenuContentProductPush extends HTMLElement {
      connectedCallback() {
        this.handleSectionChange = this.handleSectionChange || this.render.bind(this);
        this.handleDocumentClick = this.handleDocumentClick || this.closeCustomProductSelects.bind(this);
        document.addEventListener('shopify:section:load', this.handleSectionChange);
        document.addEventListener('shopify:section:reorder', this.handleSectionChange);
        document.addEventListener('click', this.handleDocumentClick);
        this.render();
      }

      disconnectedCallback() {
        document.removeEventListener('shopify:section:load', this.handleSectionChange);
        document.removeEventListener('shopify:section:reorder', this.handleSectionChange);
        document.removeEventListener('click', this.handleDocumentClick);
      }

      render() {
        const templatesByHandle = new Map();
        const targets = document.querySelectorAll('[data-menu-content-product-push-target]');

        this.querySelectorAll('template[data-menu-item-handle]').forEach((template) => {
          const handle = template.dataset.menuItemHandle;
          if (!handle || templatesByHandle.has(handle) || !template.content.childElementCount) return;
          templatesByHandle.set(handle, template);
        });

        targets.forEach((target) => {
          const targetHandles = [
            target.dataset.menuContentProductPushTarget,
            target.dataset.menuContentProductPushTargetTitle,
          ].filter(Boolean);
          const template = targetHandles
            .map((handle) => templatesByHandle.get(handle))
            .find((candidate) => candidate?.content.childElementCount);
          const isTargetActive = Boolean(template);
          const targetShell = target.closest('[data-menu-content-product-push-shell]');

          target.replaceChildren();
          target.hidden = !isTargetActive;
          if (targetShell) targetShell.hidden = !isTargetActive;

          if (isTargetActive) {
            target.appendChild(template.content.cloneNode(true));
            this.syncVariantSelectors(target);
          }
        });
      }

      syncVariantSelectors(target) {
        target.querySelectorAll('[data-menu-content-product-select]').forEach((select) => {
          this.syncVariantSelector(select);
        });
      }

      async syncVariantSelector(select) {
        if (select.dataset.menuContentProductSynced === 'true') return;
        select.dataset.menuContentProductSynced = 'true';

        if (select.dataset.menuContentProductMasterCode) {
          await this.loadMasterProductOptions(select);
        }

        this.sortProductSelectOptions(select);
        this.initCustomProductSelect(select);

        const updateVariant = () => {
          const selectedOption = select.selectedOptions[0];
          const card = select.closest('[data-menu-content-product-card]');
          const variantInput = card?.querySelector('[data-menu-content-product-variant-id]');
          const collectionIdsInput = card?.querySelector('[data-menu-content-product-collection-ids]');
          const submitButton = card?.querySelector('[data-menu-content-product-submit]');
          const productLinks = card?.querySelectorAll('[data-menu-content-product-link]');
          const primaryImage = card?.querySelector('[data-menu-content-product-image]');
          const secondaryImage = card?.querySelector('[data-menu-content-product-secondary-image]');
          const badge = card?.querySelector('[data-menu-content-product-badge]');
          const badgeText = card?.querySelector('[data-menu-content-product-badge-text]');
          const priceItem = card?.querySelector('[data-menu-content-product-price]');
          const compareItem = card?.querySelector('[data-menu-content-product-compare]');
          const availability = card?.querySelector('[data-menu-content-product-availability]');
          const availabilityText = card?.querySelector('[data-menu-content-product-availability-text]');
          const hasSelectedOption = Boolean(selectedOption);
          const isAvailable = hasSelectedOption && selectedOption.dataset.available === 'true';
          const isPurchasable = isAvailable && Number(selectedOption?.dataset.priceAmount || 0) > 0;
          const productTitle = selectedOption?.dataset.productTitle;
          const productUrl = selectedOption?.dataset.productUrl;

          Array.from(select.options).forEach((option) => option.toggleAttribute('selected', option === selectedOption));
          this.updateCustomProductSelect(select, selectedOption);

          if (variantInput && selectedOption) {
            variantInput.value = selectedOption.value;
            variantInput.disabled = !isPurchasable;
          }

          if (collectionIdsInput && selectedOption) {
            collectionIdsInput.value = selectedOption.dataset.collectionIds || '';
          }

          if (submitButton) {
            submitButton.toggleAttribute('disabled', !isPurchasable);
            submitButton.setAttribute('aria-disabled', String(!isPurchasable));
          }

          if (availability) {
            availability.classList.toggle('hidden', !hasSelectedOption || isAvailable);
          }

          if (availabilityText && selectedOption) {
            availabilityText.textContent = selectedOption.dataset.unavailableLabel || availabilityText.textContent;
          }

          if (productLinks?.length) {
            productLinks.forEach((productLink) => {
              if (productTitle && productLink.classList.contains('menu-content-product-card__title')) {
                productLink.textContent = productTitle;
              }

              if (productUrl) productLink.href = productUrl;
            });
          }

          if (selectedOption?.dataset.imageSrc) {
            this.updateProductImage(primaryImage, {
              src: selectedOption.dataset.imageSrc,
              srcset: selectedOption.dataset.imageSrcset,
              alt: selectedOption.dataset.imageAlt,
              width: selectedOption.dataset.imageWidth,
              height: selectedOption.dataset.imageHeight,
            });
          }

          if (selectedOption?.dataset.secondaryImageSrc) {
            this.updateProductImage(this.getOrCreateSecondaryImage(card), {
              src: selectedOption.dataset.secondaryImageSrc,
              srcset: selectedOption.dataset.secondaryImageSrcset,
              alt: selectedOption.dataset.secondaryImageAlt,
              width: selectedOption.dataset.secondaryImageWidth,
              height: selectedOption.dataset.secondaryImageHeight,
            });
          } else if (secondaryImage) {
            secondaryImage.hidden = true;
          }

          const productMedia = card?.querySelector('[data-menu-content-product-media]');
          productMedia?.classList.toggle(
            'menu-content-product-card__media--has-secondary',
            Boolean(selectedOption?.dataset.secondaryImageSrc),
          );

          if (badge && badgeText) {
            const productBadge = selectedOption?.dataset.productBadge || '';
            badgeText.textContent = productBadge;
            badge.classList.toggle('hidden', !productBadge);
          }

          if (selectedOption?.dataset.price && priceItem) {
            priceItem.textContent = selectedOption.dataset.price;
          }

          if (compareItem) {
            compareItem.textContent = selectedOption?.dataset.compareAtPrice || '';
            compareItem.classList.toggle('hidden', !selectedOption?.dataset.compareAtPrice);
          }
        };

        select.addEventListener('change', updateVariant);
        updateVariant();
      }

      async loadMasterProductOptions(select) {
        const masterCode = select.dataset.menuContentProductMasterCode;
        const collectionUrl = select.dataset.menuContentProductCollectionUrl;
        if (!masterCode || !collectionUrl) return;

        try {
          const optionData = await this.fetchMasterProductOptionData(masterCode, collectionUrl);
          const options = this.getMasterProductOptions(optionData.optionsHtml);
          if (!options.length) {
            select.replaceChildren();
            select.disabled = true;
            return;
          }

          select.disabled = false;
          options.sort((optionA, optionB) => this.compareProductOptions(optionA, optionB));
          select.replaceChildren(...options);

          const selectedOption = this.getPreferredProductOption(
            select,
            select.dataset.menuContentProductCurrentVariantId,
          );

          if (selectedOption) {
            selectedOption.selected = true;
            selectedOption.setAttribute('selected', 'selected');
          }

          if (optionData.productCount === 1) {
            this.renderStaticProductOption(select, selectedOption);
          }
        } catch (error) {
          console.error('Failed to load menu content product options:', error);
        }
      }

      renderStaticProductOption(select, selectedOption) {
        const selectWrap = select.closest('.menu-content-product-card__select-wrap');
        if (!selectWrap || !selectedOption) return;

        const staticVolume = document.createElement('div');
        staticVolume.className = 'menu-content-product-card__volume';
        staticVolume.textContent = selectedOption.textContent.trim();

        selectWrap.hidden = true;
        selectWrap.after(staticVolume);
      }

      fetchMasterProductOptionData(masterCode, collectionUrl) {
        this.masterProductOptionsCache = this.masterProductOptionsCache || new Map();
        const cacheKey = `${collectionUrl}/${masterCode}`;

        if (!this.masterProductOptionsCache.has(cacheKey)) {
          const request = fetch(
            `${collectionUrl}/${encodeURIComponent(masterCode)}?sections=menu-content-product-variants-data`,
          )
            .then((response) => {
              if (!response.ok || response.redirected) return '';
              return response.text();
            })
            .then((text) => {
              if (!text) return { optionsHtml: '', productCount: 0 };

              const responseJSON = JSON.parse(text);
              const htmlString = responseJSON['menu-content-product-variants-data'];
              if (!htmlString) return { optionsHtml: '', productCount: 0 };

              const parser = new DOMParser();
              const doc = parser.parseFromString(htmlString, 'text/html');
              const template = doc.querySelector('template[data-menu-content-product-options]');

              return {
                optionsHtml: template?.innerHTML || '',
                productCount: Number(template?.dataset.productCount || 0),
              };
            });

          this.masterProductOptionsCache.set(cacheKey, request);
        }

        return this.masterProductOptionsCache.get(cacheKey);
      }

      initCustomProductSelect(select) {
        const customSelect = this.getCustomProductSelect(select);
        if (!customSelect) return;

        this.renderCustomProductSelect(select);

        if (customSelect.dataset.menuContentProductCustomSynced === 'true') return;
        customSelect.dataset.menuContentProductCustomSynced = 'true';

        const selectedButton = customSelect.querySelector('[data-menu-content-product-selected-option]');
        const optionsList = customSelect.querySelector('[data-menu-content-product-options-list]');

        selectedButton?.addEventListener('click', (event) => {
          event.stopPropagation();
          const shouldOpen = !customSelect.classList.contains('is-open');

          this.closeCustomProductSelects();
          this.setCustomProductSelectOpen(customSelect, shouldOpen);
        });

        selectedButton?.addEventListener('keydown', (event) => {
          if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
          event.preventDefault();
          this.setCustomProductSelectOpen(customSelect, true);

          const optionButtons = Array.from(customSelect.querySelectorAll('[data-menu-content-product-option-button]'));
          const optionToFocus = event.key === 'ArrowUp' ? optionButtons[optionButtons.length - 1] : optionButtons[0];
          optionToFocus?.focus();
        });

        optionsList?.addEventListener('click', (event) => {
          const optionButton = event.target.closest('[data-menu-content-product-option-button]');
          if (!optionButton) return;

          this.selectCustomProductOption(select, optionButton.dataset.value);
        });

        optionsList?.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            this.setCustomProductSelectOpen(customSelect, false);
            selectedButton?.focus();
          }
        });
      }

      renderCustomProductSelect(select) {
        const customSelect = this.getCustomProductSelect(select);
        if (!customSelect) return;

        const optionsList = customSelect.querySelector('[data-menu-content-product-options-list]');
        const selectedButton = customSelect.querySelector('[data-menu-content-product-selected-option]');
        const selectedOption = select.selectedOptions[0] || select.options[0];

        if (!optionsList) return;

        this.updateProductSelectLayout(select);

        optionsList.replaceChildren(
          ...Array.from(select.options).map((option) => {
            const optionButton = document.createElement('button');
            const optionLabel = document.createElement('span');

            optionButton.type = 'button';
            optionButton.className = 'menu-content-product-card__option';
            optionButton.dataset.menuContentProductOptionButton = '';
            optionButton.dataset.value = option.value;
            optionButton.setAttribute('role', 'option');
            optionButton.setAttribute('aria-selected', String(option === selectedOption));

            optionLabel.className = 'menu-content-product-card__option-label';
            optionLabel.textContent = option.textContent.trim();
            optionButton.appendChild(optionLabel);

            return optionButton;
          }),
        );

        customSelect.hidden = select.disabled || !select.options.length;
        if (selectedButton) selectedButton.disabled = select.disabled || !select.options.length;
        this.updateCustomProductSelect(select, selectedOption);
      }

      updateCustomProductSelect(select, selectedOption) {
        const customSelect = this.getCustomProductSelect(select);
        if (!customSelect) return;

        const selectedText = customSelect.querySelector('[data-menu-content-product-selected-text]');

        if (selectedText && selectedOption) {
          selectedText.textContent = selectedOption.textContent.trim();
        }

        customSelect.querySelectorAll('[data-menu-content-product-option-button]').forEach((optionButton) => {
          const isSelected = selectedOption && optionButton.dataset.value === selectedOption.value;
          optionButton.classList.toggle('is-selected', Boolean(isSelected));
          optionButton.setAttribute('aria-selected', String(Boolean(isSelected)));
        });
      }

      updateProductSelectLayout(select) {
        const selectWrap = select.closest('.menu-content-product-card__select-wrap');
        const hasDescriptiveLabel = Array.from(select.options).some(
          (option) =>
            option.dataset.hasSecondaryLabel === 'true' || !this.isCompactProductOptionLabel(option.textContent),
        );

        selectWrap?.classList.toggle('menu-content-product-card__select-wrap--full', hasDescriptiveLabel);
        selectWrap?.classList.toggle('menu-content-product-card__select-wrap--compact', !hasDescriptiveLabel);
      }

      isCompactProductOptionLabel(label) {
        const normalizedLabel = label.trim().replace(/\s+/g, ' ');

        return /^\d+(?:[.,]\d+)?\s*(?:ml|cl|l|mg|g|kg|oz|fl\.?\s*oz\.?)$/i.test(normalizedLabel);
      }

      selectCustomProductOption(select, value) {
        const customSelect = this.getCustomProductSelect(select);
        const optionToSelect = Array.from(select.options).find((option) => option.value === value);
        if (!optionToSelect) return;

        select.value = optionToSelect.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        this.setCustomProductSelectOpen(customSelect, false);
      }

      getCustomProductSelect(select) {
        return select
          .closest('.menu-content-product-card__select-wrap')
          ?.querySelector('[data-menu-content-product-custom-select]');
      }

      setCustomProductSelectOpen(customSelect, isOpen) {
        if (!customSelect) return;

        const productCard = customSelect.closest('[data-menu-content-product-card]');
        const productItem = customSelect.closest('.menu-content-product-push__item');

        productCard?.classList.toggle('menu-content-product-card--select-open', isOpen);
        productItem?.classList.toggle('menu-content-product-push__item--select-open', isOpen);
        customSelect.classList.toggle('is-open', isOpen);

        if (isOpen) {
          this.positionCustomProductSelect(customSelect);
        } else {
          this.resetCustomProductSelectPosition(customSelect);
        }

        customSelect
          .querySelector('[data-menu-content-product-selected-option]')
          ?.setAttribute('aria-expanded', String(isOpen));
      }

      positionCustomProductSelect(customSelect) {
        const optionsList = customSelect.querySelector('[data-menu-content-product-options-list]');
        const selectedButton = customSelect.querySelector('[data-menu-content-product-selected-option]');
        if (!optionsList || !selectedButton) return;

        const selectedButtonRect = selectedButton.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const menuGap = 8;
        const minimumHeight = 120;
        const preferredHeight = 220;
        const spaceBelow = viewportHeight - selectedButtonRect.bottom - menuGap;
        const spaceAbove = selectedButtonRect.top - menuGap;
        const shouldOpenUp = spaceBelow < preferredHeight && spaceAbove > spaceBelow;
        const availableSpace = Math.max(minimumHeight, shouldOpenUp ? spaceAbove : spaceBelow);

        customSelect.classList.toggle('is-open-up', shouldOpenUp);
        optionsList.style.maxHeight = `${Math.min(preferredHeight, availableSpace)}px`;
      }

      resetCustomProductSelectPosition(customSelect) {
        const optionsList = customSelect.querySelector('[data-menu-content-product-options-list]');

        customSelect.classList.remove('is-open-up');
        if (optionsList) optionsList.style.removeProperty('max-height');
      }

      closeCustomProductSelects(event) {
        if (event?.target.closest('[data-menu-content-product-custom-select]')) return;

        document
          .querySelectorAll('[data-menu-content-product-custom-select].is-open')
          .forEach((customSelect) => this.setCustomProductSelectOpen(customSelect, false));
      }

      getMasterProductOptions(optionsHtml) {
        const template = document.createElement('template');
        template.innerHTML = optionsHtml;

        return Array.from(template.content.querySelectorAll('option'));
      }

      sortProductSelectOptions(select) {
        const selectedValue = select.value;
        const options = Array.from(select.options);

        options.sort((optionA, optionB) => this.compareProductOptions(optionA, optionB));
        select.replaceChildren(...options);

        const selectedOption = this.getPreferredProductOption(select, selectedValue);
        if (selectedOption) selectedOption.selected = true;
      }

      getPreferredProductOption(select, preferredValue) {
        const options = Array.from(select.options);
        const preferredOption = options.find((option) => option.value === preferredValue);

        if (preferredOption?.dataset.available === 'true') return preferredOption;

        return options.find((option) => option.dataset.available === 'true') || preferredOption || options[0];
      }

      compareProductOptions(optionA, optionB) {
        const optionAUnavailable = optionA.dataset.available !== 'true';
        const optionBUnavailable = optionB.dataset.available !== 'true';

        if (optionAUnavailable !== optionBUnavailable) return optionAUnavailable ? 1 : -1;

        return this.compareOptionLabels(optionA.textContent, optionB.textContent);
      }

      compareOptionLabels(labelA, labelB) {
        labelA = labelA.trim();
        labelB = labelB.trim();

        const getLastNumber = (label) => {
          const matches = label.match(/\d+(?:[.,]\d+)?/g);
          const value = matches ? matches[matches.length - 1] : null;
          return parseFloat(value?.replace(',', '.')) || Infinity;
        };

        const numberA = getLastNumber(labelA);
        const numberB = getLastNumber(labelB);

        return numberA - numberB || labelA.localeCompare(labelB);
      }

      updateProductImage(image, imageData) {
        if (!image || !imageData?.src) return;

        image.src = imageData.src;
        image.hidden = false;

        if (imageData.srcset) {
          image.srcset = imageData.srcset;
        } else {
          image.removeAttribute('srcset');
        }

        if (imageData.alt != null) image.alt = imageData.alt;
        if (imageData.width) image.setAttribute('width', imageData.width);
        if (imageData.height) image.setAttribute('height', imageData.height);
      }

      getOrCreateSecondaryImage(card) {
        const productMedia = card?.querySelector('[data-menu-content-product-media]');
        if (!productMedia) return null;

        const existingImage = productMedia.querySelector('[data-menu-content-product-secondary-image]');
        if (existingImage) return existingImage;

        const image = document.createElement('img');
        image.className = 'menu-content-product-card__image menu-content-product-card__image--secondary';
        image.loading = 'lazy';
        image.sizes = '(min-width: 990px) 24rem, 70vw';
        image.setAttribute('data-menu-content-product-secondary-image', '');
        productMedia.appendChild(image);

        return image;
      }
    },
  );
}
